import React, { useState } from 'react';
import { Archive, ArchiveRestore, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import type { CompanyPage } from '@/hooks/useManagementAPI';
import { ArchiveCompanyModal } from './ArchiveCompanyModal';

interface CompanyArchiveSectionProps {
  companyName: string;
  pages: CompanyPage[];
  onArchiveAllPages: () => Promise<{ success: number; failed: number; errors: string[] }>;
  onUnarchiveAllPages: () => Promise<{ success: number; failed: number; errors: string[] }>;
}

export const CompanyArchiveSection: React.FC<CompanyArchiveSectionProps> = ({
  companyName,
  pages,
  onArchiveAllPages,
  onUnarchiveAllPages,
}) => {
  const { toast } = useToast();
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Check if company is archived (all pages archived)
  const isArchived = pages.length > 0 && pages.every(p => p.archived);

  // Archive all pages handler
  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      const result = await onArchiveAllPages();

      if (result.failed > 0) {
        toast({
          title: "Partial Success",
          description: `Archived ${result.success} of ${result.success + result.failed} pages. ${result.failed} failed.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Company Archived",
          description: `Successfully archived ${result.success} pages.`,
        });
      }
    } catch (error) {
      console.error('[CompanyArchiveSection] Archive error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to archive pages',
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
      setShowArchiveModal(false);
    }
  };

  // Unarchive all pages handler
  const handleUnarchive = async () => {
    setIsArchiving(true);
    try {
      const result = await onUnarchiveAllPages();

      if (result.failed > 0) {
        toast({
          title: "Partial Success",
          description: `Unarchived ${result.success} of ${result.success + result.failed} pages. ${result.failed} failed.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Company Unarchived",
          description: `Successfully unarchived ${result.success} pages.`,
        });
      }
    } catch (error) {
      console.error('[CompanyArchiveSection] Unarchive error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to unarchive pages',
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {isArchived ? (
              <Archive className="w-5 h-5 text-orange-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-gray-400" />
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              Archive Company
            </h2>
            {isArchived && (
              <Badge variant="secondary" className="bg-orange-100 text-orange-700 border-orange-200">
                ARCHIVED
              </Badge>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-4">
            {isArchived ? (
              <>
                This company is currently archived. All <span className="font-semibold">{pages.length}</span> pricing page{pages.length !== 1 ? 's' : ''} are hidden from lists and analytics.
              </>
            ) : (
              <>
                Archive this company to hide all pricing pages from lists and analytics. This will archive <span className="font-semibold">{pages.length}</span> page{pages.length !== 1 ? 's' : ''}.
              </>
            )}
          </p>

          {isArchived && pages.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-orange-800">
                <span className="font-medium">Archived pages:</span> {pages.map(p => p.subslug || '(main)').join(', ')}
              </p>
            </div>
          )}
        </div>

        <div>
          {isArchived ? (
            <Button
              onClick={handleUnarchive}
              disabled={isArchiving}
              variant="outline"
            >
              <ArchiveRestore className="h-4 w-4 mr-2" />
              Unarchive Company
            </Button>
          ) : (
            <Button
              onClick={() => setShowArchiveModal(true)}
              disabled={isArchiving || pages.length === 0}
              variant="outline"
              className="text-orange-600 border-orange-300 hover:bg-orange-50 hover:text-orange-700"
            >
              <Archive className="h-4 w-4 mr-2" />
              Archive Company
            </Button>
          )}
        </div>
      </div>

      {/* Archive Confirmation Modal */}
      <ArchiveCompanyModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchive}
        companyName={companyName}
        pages={pages}
        isLoading={isArchiving}
      />
    </div>
  );
};
